import { type Mixpanel, type Config } from 'mixpanel-browser';
import mixpanel from 'mixpanel-browser';
import { createContext, ProviderProps, useMemo } from 'react';

import chromeExtensionVersion from '@/utils/chromeExtensionVersion';
import isInChromeExtension from '@/utils/isInChromeExtension';

type ProviderPropsWithoutValue<T> = Omit<ProviderProps<T>, 'value'>;

const DUGOUT_MIXPANEL_TOKEN = process.env.VITE_APP_DUGOUT_MIXPANEL_TOKEN;

export type InternalMixpanelContext = Mixpanel | undefined;
export const internalMixpanelContext = createContext<InternalMixpanelContext>(undefined);

export interface InternalMixpanelProviderProps extends ProviderPropsWithoutValue<InternalMixpanelContext> {
  config?: Partial<Config>;
}

/**
 * Separate Mixpanel project for Dugout (the internal, staff-facing app) so its usage data stays
 * segmentable from the member-facing onboarding project set up in mixpanelContext.tsx.
 *
 * `track_pageview: 'full-url'` uses Mixpanel's built-in SPA router integration (it listens to
 * pushState/popstate) instead of us wiring up route-change tracking by hand.
 *
 * The "in the Chrome extension" flag is registered as a super property right here, synchronously,
 * immediately after init - so it lands on every event this instance ever sends, rather than
 * depending on some downstream component's effect running first.
 */
export const InternalMixpanelProvider = ({
  children,
  config: _config
}: InternalMixpanelProviderProps) => {
  const config = useMemo(
    () => ({
      autocapture: {
        click: true,
        input: true,
        rage_click: true,
        dead_click: true,
        submit: true,
        scroll: false,
        capture_text_content: true // captures text inside clickable elements that were clicked. These shouldn't contain PII
      },
      track_pageview: 'full-url' as const,
      ..._config
    }),
    [_config]
  );

  const value = useMemo(() => {
    if (!DUGOUT_MIXPANEL_TOKEN) {
      return undefined;
    }

    const instance = mixpanel.init(DUGOUT_MIXPANEL_TOKEN, config, 'dugout-internal');
    instance.register({
      isChromeExtension: isInChromeExtension(),
      chromeExtensionVersion: chromeExtensionVersion() ?? undefined
    });

    return instance;
  }, [config]);

  return (
    <internalMixpanelContext.Provider value={value}>{children}</internalMixpanelContext.Provider>
  );
};
